// Formatting utilities

import { format, formatDistanceToNow } from 'date-fns';

/**
 * Parse a Java LocalDateTime serialized as a number array [year, month(1-based), day, hour, min, sec, nano]
 * OR an ISO string — always returns a Date in UTC.
 */
export function parseJavaUtcDateTime(val: number[] | string | null | undefined): Date | null {
  if (!val) return null;
  if (Array.isArray(val)) {
    const [y, m, d, h = 0, min = 0, s = 0] = val;
    return new Date(Date.UTC(y, m - 1, d, h, min, s)); // Java months are 1-based
  }
  const date = new Date(val);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a Java UTC datetime (array or ISO string) as a human-readable UTC string.
 * Returns 'N/A' if the value is absent or unparseable.
 */
export function formatJavaUtcDateTime(val: number[] | string | null | undefined): string {
  const date = parseJavaUtcDateTime(val);
  if (!date) return 'N/A';
  return date.toLocaleString('en-GB', { timeZone: 'UTC' });
}

export const formatDate = (date: string | Date): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'MMM dd, yyyy HH:mm');
  } catch {
    return 'Invalid date';
  }
};

export const formatRelativeTime = (date: string | Date): string => {
  if (!date) return 'N/A';
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  } catch {
    return 'Invalid date';
  }
};

export const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  return `${(ms / 60000).toFixed(2)}m`;
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat().format(num);
};

export const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
